import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Briefcase,
  MapPin,
  Building2,
  Factory,
  Sparkles,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Loader2,
  Zap,
  Target,
  Search,
  Users,
  Globe,
  SlidersHorizontal,
  Clock,
  BarChart3,
  Check,
  Layers,
  MinusCircle,
  MessageSquare,
} from 'lucide-react';
import api from '../../utils/axios';

// ═══════════════════════════════════════════════════════════
// TAXONOMY CONSTANTS — Exact values ContactOut API accepts
// These MUST be used as-is in API payloads (no free text!)
// ═══════════════════════════════════════════════════════════

const SENIORITY_OPTIONS = [
  { value: 'Owner / Founder', label: 'Owner / Founder' },
  { value: 'CXO', label: 'C-Level / CXO' },
  { value: 'Partner', label: 'Partner' },
  { value: 'VP', label: 'VP' },
  { value: 'Head', label: 'Head' },
  { value: 'Director', label: 'Director' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Senior', label: 'Senior' },
  { value: 'Entry', label: 'Entry Level' },
  { value: 'Intern', label: 'Intern' },
];

const DEPARTMENT_OPTIONS = [
  { value: 'Operations', label: 'Operations' },
  { value: 'Business Development', label: 'Business Development' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Education', label: 'Education' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Healthcare Services', label: 'Healthcare Services' },
  { value: 'Information Technology', label: 'IT' },
  { value: 'Administrative', label: 'Administrative' },
  { value: 'Arts and Design', label: 'Arts & Design' },
  { value: 'Customer Success and Support', label: 'Customer Success' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Community and Social Services', label: 'Community Services' },
  { value: 'Media and Communication', label: 'Media & Comms' },
  { value: 'Accounting', label: 'Accounting' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Human Resources', label: 'Human Resources' },
  { value: 'Research', label: 'Research' },
  { value: 'Program and Project Management', label: 'Program / Project Mgmt' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Military and Protective Services', label: 'Military & Protective' },
  { value: 'Consulting', label: 'Consulting' },
  { value: 'Entrepreneurship', label: 'Entrepreneurship' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Quality Assurance', label: 'Quality Assurance' },
  { value: 'Purchasing', label: 'Purchasing' },
  { value: 'Product Management', label: 'Product Management' },
  { value: 'Leadership', label: 'Leadership' },
];

const HEADCOUNT_OPTIONS = [
  { value: '1_10', label: '1–10' },
  { value: '11_50', label: '11–50' },
  { value: '51_200', label: '51–200' },
  { value: '201_500', label: '201–500' },
  { value: '501_1000', label: '501–1K' },
  { value: '1001_5000', label: '1K–5K' },
  { value: '5001_10000', label: '5K–10K' },
  { value: '10001', label: '10K+' },
];

const EXPERIENCE_OPTIONS = [
  { value: '0_1', label: '< 1 year' },
  { value: '1_2', label: '1–2 years' },
  { value: '3_5', label: '3–5 years' },
  { value: '6_10', label: '6–10 years' },
  { value: '10', label: '10+ years' },
];

// ContactOut's industry taxonomy — exact values from ContactOut API CSV
const INDUSTRY_OPTIONS = [
  'Accounting',
  'Airlines/Aviation',
  'Alternative Dispute Resolution',
  'Alternative Medicine',
  'Animation',
  'Apparel & Fashion',
  'Architecture & Planning',
  'Arts & Crafts',
  'Automotive',
  'Aviation & Aerospace',
  'Banking',
  'Biotechnology',
  'Broadcast Media',
  'Building Materials',
  'Business Supplies & Equipment',
  'Capital Markets',
  'Chemicals',
  'Civic & Social Organization',
  'Civil Engineering',
  'Commercial Real Estate',
  'Computer & Network Security',
  'Computer Games',
  'Computer Hardware',
  'Computer Networking',
  'Computer Software',
  'Construction',
  'Consumer Electronics',
  'Consumer Goods',
  'Consumer Services',
  'Cosmetics',
  'Dairy',
  'Defense & Space',
  'Design',
  'E-learning',
  'Education Management',
  'Electrical & Electronic Manufacturing',
  'Entertainment',
  'Environmental Services',
  'Events Services',
  'Executive Office',
  'Facilities Services',
  'Farming',
  'Financial Services',
  'Fine Art',
  'Fishery',
  'Food & Beverages',
  'Food Production',
  'Fundraising',
  'Furniture',
  'Gambling & Casinos',
  'Glass, Ceramics & Concrete',
  'Government Administration',
  'Government Relations',
  'Graphic Design',
  'Health Care',
  'Health, Wellness & Fitness',
  'Higher Education',
  'Hospital & Health Care',
  'Hospitality',
  'Human Resources',
  'Import & Export',
  'Individual & Family Services',
  'Industrial Automation',
  'Information Services',
  'Information Technology & Services',
  'Insurance',
  'International Affairs',
  'International Trade & Development',
  'Internet',
  'Investment Banking',
  'Investment Management',
  'Judiciary',
  'Law Enforcement',
  'Law Practice',
  'Legal Services',
  'Legislative Office',
  'Leisure, Travel & Tourism',
  'Libraries',
  'Logistics & Supply Chain',
  'Luxury Goods & Jewelry',
  'Machinery',
  'Management Consulting',
  'Maritime',
  'Market Research',
  'Marketing & Advertising',
  'Mechanical Or Industrial Engineering',
  'Media Production',
  'Medical Devices',
  'Medical Practice',
  'Mental Health Care',
  'Military',
  'Mining & Metals',
  'Motion Pictures & Film',
  'Museums & Institutions',
  'Music',
  'Nanotechnology',
  'Newspapers',
  'Non-profit Organization Management',
  'Oil & Energy',
  'Online Media',
  'Outsourcing/Offshoring',
  'Package/Freight Delivery',
  'Packaging & Containers',
  'Paper & Forest Products',
  'Performing Arts',
  'Pharmaceuticals',
  'Philanthropy',
  'Photography',
  'Plastics',
  'Political Organization',
  'Primary/Secondary Education',
  'Printing',
  'Professional Training & Coaching',
  'Program Development',
  'Public Policy',
  'Public Relations & Communications',
  'Public Safety',
  'Publishing',
  'Railroad Manufacture',
  'Ranching',
  'Real Estate',
  'Recreational Facilities & Services',
  'Religious Institutions',
  'Renewables & Environment',
  'Research',
  'Restaurants',
  'Retail',
  'Security & Investigations',
  'Semiconductors',
  'Shipbuilding',
  'Sporting Goods',
  'Sports',
  'Staffing & Recruiting',
  'Supermarkets',
  'Telecommunications',
  'Textiles',
  'Think Tanks',
  'Tobacco',
  'Translation & Localization',
  'Transportation/Trucking/Railroad',
  'Utilities',
  'Venture Capital & Private Equity',
  'Veterinary',
  'Warehousing',
  'Wholesale',
  'Wine & Spirits',
  'Wireless',
  'Writing & Editing',
];

// Related industry groups — when user picks one, suggest nearby ones
const INDUSTRY_GROUPS = {
  'Medical Devices': ['Pharmaceuticals', 'Biotechnology', 'Hospital & Health Care', 'Health Care', 'Health, Wellness & Fitness'],
  'Pharmaceuticals': ['Medical Devices', 'Biotechnology', 'Hospital & Health Care', 'Health, Wellness & Fitness'],
  'Biotechnology': ['Pharmaceuticals', 'Medical Devices', 'Hospital & Health Care', 'Health, Wellness & Fitness'],
  'Hospital & Health Care': ['Medical Devices', 'Pharmaceuticals', 'Biotechnology', 'Health, Wellness & Fitness'],
  'Health Care': ['Medical Devices', 'Pharmaceuticals', 'Biotechnology', 'Hospital & Health Care'],
  'Health, Wellness & Fitness': ['Medical Devices', 'Pharmaceuticals', 'Biotechnology', 'Hospital & Health Care'],
  'Computer Software': ['Information Technology & Services', 'Internet'],
  'Information Technology & Services': ['Computer Software', 'Internet', 'Telecommunications'],
  'Financial Services': ['Banking', 'Insurance', 'Investment Banking', 'Capital Markets'],
  'Banking': ['Financial Services', 'Insurance', 'Investment Banking'],
  'Automotive': ['Industrial Automation', 'Machinery', 'Electrical & Electronic Manufacturing'],
  'Real Estate': ['Construction', 'Commercial Real Estate'],
  'Oil & Energy': ['Renewables & Environment', 'Utilities', 'Mining & Metals'],
  'Retail': ['Consumer Goods', 'Consumer Services', 'Food & Beverages'],
  'Education Management': ['Higher Education', 'E-learning', 'Research', 'Primary/Secondary Education'],
  'Higher Education': ['Education Management', 'E-learning', 'Research'],
  'Telecommunications': ['Information Technology & Services', 'Wireless', 'Internet'],
};

// Top countries (pinned at top of selector, rest alphabetical)
const TOP_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'India',
  'Netherlands',
  'Singapore',
  'Ireland',
];

const ALL_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh',
  'Belarus', 'Belgium', 'Bolivia', 'Bosnia and Herzegovina', 'Brazil',
  'Bulgaria', 'Cambodia', 'Cameroon', 'Canada', 'Chile', 'China', 'Colombia',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Estonia',
  'Ethiopia', 'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece',
  'Guatemala', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica',
  'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia', 'Lebanon',
  'Libya', 'Lithuania', 'Luxembourg', 'Malaysia', 'Malta', 'Mexico',
  'Moldova', 'Monaco', 'Mongolia', 'Morocco', 'Mozambique', 'Myanmar',
  'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Nigeria', 'Norway',
  'Oman', 'Pakistan', 'Panama', 'Paraguay', 'Peru', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea',
  'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan', 'Tanzania',
  'Thailand', 'Tunisia', 'Turkey', 'UAE', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay',
  'Uzbekistan', 'Venezuela', 'Vietnam', 'Zimbabwe',
];


// ═══════════════════════════════════════════════════════════
// REUSABLE UI COMPONENTS
// ═══════════════════════════════════════════════════════════

/**
 * Multi-select chip selector — for constrained value sets (seniority, department, headcount, etc.)
 */
function ChipSelector({ options, selected, onChange, color = 'emerald', size = 'sm' }) {
  const toggle = useCallback((val) => {
    onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  }, [onChange]);

  const colorMap = {
    emerald: { active: 'bg-emerald-100 text-emerald-700 border-emerald-200', hover: 'hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200' },
    violet: { active: 'bg-violet-100 text-violet-700 border-violet-200', hover: 'hover:bg-violet-50 hover:text-violet-600 hover:border-violet-200' },
    blue: { active: 'bg-blue-100 text-blue-700 border-blue-200', hover: 'hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200' },
    amber: { active: 'bg-amber-100 text-amber-700 border-amber-200', hover: 'hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200' },
    slate: { active: 'bg-slate-100 text-slate-700 border-slate-200', hover: 'hover:bg-slate-50 hover:text-slate-600 hover:border-slate-200' },
  };
  const c = colorMap[color] || colorMap.emerald;
  const textSize = size === 'xs' ? 'text-[10px]' : 'text-xs';
  const padding = size === 'xs' ? 'px-2 py-0.5' : 'px-2.5 py-1';

  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const val = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const isActive = selected.includes(val);
        return (
          <button
            key={val}
            onClick={() => toggle(val)}
            className={`inline-flex items-center gap-1 ${padding} rounded-full ${textSize} font-medium border transition-all duration-150
              ${isActive
                ? `${c.active} shadow-sm`
                : `bg-black/[0.02] text-black/40 border-black/[0.06] ${c.hover}`
              }`}
          >
            {isActive && <Check className="w-3 h-3" />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Searchable dropdown for large lists (industry, country)
 * Supports multi-select. Shows selected items as pills above the input.
 */
function SearchableMultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Search...',
  color = 'emerald',
  icon: Icon = null,
  pinnedOptions = [],
  suggestions = [],
  onSuggestionsDismiss,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const colorMap = {
    emerald: { pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', ring: 'focus:border-emerald-300 focus:ring-emerald-200' },
    blue: { pill: 'bg-blue-100 text-blue-700 border-blue-200', ring: 'focus:border-blue-300 focus:ring-blue-200' },
    violet: { pill: 'bg-violet-100 text-violet-700 border-violet-200', ring: 'focus:border-violet-300 focus:ring-violet-200' },
    amber: { pill: 'bg-amber-100 text-amber-700 border-amber-200', ring: 'focus:border-amber-300 focus:ring-amber-200' },
    slate: { pill: 'bg-slate-100 text-slate-700 border-slate-200', ring: 'focus:border-slate-300 focus:ring-slate-200' },
  };
  const c = colorMap[color] || colorMap.emerald;

  // Filter options by search text, exclude already-selected
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const available = options.filter(o => !selected.includes(o));
    if (!q) {
      // Show pinned first, then rest
      const pinned = pinnedOptions.filter(p => available.includes(p));
      const rest = available.filter(p => !pinnedOptions.includes(p));
      return [...pinned, ...rest].slice(0, 40);
    }
    return available.filter(o => o.toLowerCase().includes(q)).slice(0, 30);
  }, [options, selected, search, pinnedOptions]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addItem = useCallback((item) => {
    if (!selected.includes(item)) {
      onChange([...selected, item]);
    }
    setSearch('');
    if (onSuggestionsDismiss) onSuggestionsDismiss(item);
  }, [selected, onChange, onSuggestionsDismiss]);

  const removeItem = useCallback((item) => {
    onChange(selected.filter(s => s !== item));
  }, [selected, onChange]);

  return (
    <div ref={containerRef} className="relative">
      {/* Selected pills */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {selected.map((item) => (
            <span key={item} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${c.pill} border`}>
              {item}
              <button onClick={() => removeItem(item)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Suggestion pills */}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {suggestions.filter(s => !selected.includes(s)).map((sug) => (
            <button key={sug} onClick={() => addItem(sug)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-black/[0.03] text-black/40 border border-dashed border-black/10 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-all">
              <Plus className="w-2.5 h-2.5" />{sug}
            </button>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          className={`w-full text-xs bg-white border border-black/[0.06] rounded-lg ${Icon ? 'pl-7' : 'pl-3'} pr-8 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:ring-1 ${c.ring} transition-all`}
          placeholder={placeholder}
        />
        {Icon && <Icon className="w-3 h-3 text-black/20 absolute left-2.5 top-1/2 -translate-y-1/2" />}
        <button
          onClick={() => { setIsOpen(!isOpen); if (!isOpen) inputRef.current?.focus(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-black/25 hover:text-black/50 transition-colors"
        >
          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
          {filtered.map((item) => (
            <button
              key={item}
              onClick={() => addItem(item)}
              className="w-full text-left px-3 py-1.5 text-xs text-black/70 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-3 h-3 opacity-40" />
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Tag input for free-text multi-value fields (skills, companies, locations)
 * Comma or Enter to add. Shows as pills.
 */
function TagInput({ values, onChange, placeholder, color = 'violet', icon: Icon = null }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const colorMap = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    violet: 'bg-violet-100 text-violet-700 border-violet-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    teal: 'bg-teal-100 text-teal-700 border-teal-200',
  };
  const pillColor = colorMap[color] || colorMap.violet;
  const ringColor = {
    emerald: 'focus:border-emerald-300 focus:ring-emerald-200',
    violet: 'focus:border-violet-300 focus:ring-violet-200',
    blue: 'focus:border-blue-300 focus:ring-blue-200',
    amber: 'focus:border-amber-300 focus:ring-amber-200',
    teal: 'focus:border-teal-300 focus:ring-teal-200',
  }[color] || 'focus:border-violet-300 focus:ring-violet-200';

  const addItems = useCallback((text) => {
    const newItems = text.split(',').map(s => s.trim()).filter(s => s && !values.includes(s));
    if (newItems.length > 0) {
      onChange([...values, ...newItems]);
    }
    setInput('');
  }, [values, onChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) addItems(input);
    }
    if (e.key === 'Backspace' && !input && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeItem = useCallback((item) => {
    onChange(values.filter(v => v !== item));
  }, [values, onChange]);

  return (
    <div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {values.map((item) => (
            <span key={item} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${pillColor} border`}>
              {item}
              <button onClick={() => removeItem(item)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-full text-xs bg-white border border-black/[0.06] rounded-lg ${Icon ? 'pl-7' : 'pl-3'} pr-3 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:ring-1 ${ringColor} transition-all`}
            placeholder={placeholder}
          />
          {Icon && <Icon className="w-3 h-3 text-black/20 absolute left-2.5 top-1/2 -translate-y-1/2" />}
        </div>
        {input.trim() && (
          <button onClick={() => addItems(input)}
            className={`text-xs px-2.5 py-1.5 rounded-lg ${pillColor.replace('border-', 'hover:bg-').split(' ')[0]} transition-colors`}>
            Add
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Company autocomplete — Async search powered by ZoomInfo company search.
 * User types → debounced API call → dropdown with company names, industry, size.
 * Selected companies appear as pills. Free-text entry still allowed via Enter/comma.
 */
function CompanyAutocomplete({ values, onChange, placeholder, color = 'blue', icon: Icon = Building2, excludedValues = [], onExcludeChange, onToggleExclude }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const colorMap = {
    blue: { pill: 'bg-blue-100 text-blue-700 border-blue-200', ring: 'focus:border-blue-300 focus:ring-blue-200' },
    emerald: { pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', ring: 'focus:border-emerald-300 focus:ring-emerald-200' },
    red: { pill: 'bg-red-100 text-red-700 border-red-200', ring: 'focus:border-red-300 focus:ring-red-200' },
  };
  const c = colorMap[color] || colorMap.blue;

  // Debounced search
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const { data } = await api.post('/api/ai/company-suggest', { query: q }, { timeout: 8000 });
        if (data.success && data.companies?.length > 0) {
          setSuggestions(data.companies.filter(co => !values.includes(co.name)));
          setIsOpen(true);
        } else {
          setSuggestions([]);
        }
      } catch (e) {
        console.warn('Company suggest failed:', e.message);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 350);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [input, values]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addCompany = useCallback((name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // Detect exclusion prefix: "-Google" or "- Google"
    if (trimmed.startsWith('-') && onExcludeChange) {
      const excluded = trimmed.replace(/^-\s*/, '').trim();
      if (excluded && !excludedValues.includes(excluded)) {
        onExcludeChange([...excludedValues, excluded]);
      }
    } else if (!values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput('');
    setIsOpen(false);
    setSuggestions([]);
  }, [values, onChange, excludedValues, onExcludeChange]);

  const removeCompany = useCallback((name) => {
    onChange(values.filter(v => v !== name));
  }, [values, onChange]);

  const removeExcludedCompany = useCallback((name) => {
    if (onExcludeChange) onExcludeChange(excludedValues.filter(v => v !== name));
  }, [excludedValues, onExcludeChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) addCompany(input);
    }
    if (e.key === 'Backspace' && !input && values.length > 0) {
      onChange(values.slice(0, -1));
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const formatSize = (count) => {
    if (!count) return '';
    if (count >= 10000) return `${Math.round(count / 1000)}K`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return String(count);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Selected pills (includes + excludes) */}
      {(values.length > 0 || excludedValues.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {values.map((name) => (
            <span key={name} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${c.pill} border cursor-pointer hover:opacity-80 transition-all`} title="Click to exclude">
              <Building2 className="w-2.5 h-2.5" />
              <span onClick={() => onToggleExclude?.(name)}>{name}</span>
              <button onClick={() => removeCompany(name)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {excludedValues.map((name) => (
            <span key={`excl-${name}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200 line-through decoration-red-400/50 cursor-pointer hover:bg-red-200/70 transition-colors" title="Click to include">
              <MinusCircle className="w-2.5 h-2.5" />
              <span onClick={() => onToggleExclude?.(name)}>{name}</span>
              <button onClick={() => removeExcludedCompany(name)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); if (!isOpen && e.target.value.trim().length >= 2) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          className={`w-full text-xs bg-white border border-black/[0.06] rounded-lg pl-7 pr-8 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:ring-1 ${c.ring} transition-all`}
          placeholder={placeholder}
        />
        <Icon className="w-3 h-3 text-black/20 absolute left-2.5 top-1/2 -translate-y-1/2" />
        {isLoading && <Loader2 className="w-3 h-3 text-black/30 animate-spin absolute right-2.5 top-1/2 -translate-y-1/2" />}
        {!isLoading && input.trim() && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-black/25">enter to add</span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-black/[0.08] shadow-lg max-h-56 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
          {suggestions.map((co, i) => (
            <button
              key={`${co.name}-${i}`}
              onClick={() => addCompany(co.name)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2.5 border-b border-black/[0.03] last:border-0"
            >
              {co.logo ? (
                <img src={co.logo} alt="" className="w-5 h-5 rounded object-contain bg-white" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <Building2 className="w-4 h-4 opacity-30 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-black/80 truncate">{co.name}</div>
                <div className="flex items-center gap-2 text-[10px] text-black/40">
                  {co.industry && <span>{co.industry}</span>}
                  {co.employeeCount && <span>{formatSize(co.employeeCount)} employees</span>}
                  {co.city && co.country && <span>{co.city}, {co.country}</span>}
                </div>
              </div>
              <Plus className="w-3 h-3 opacity-30 flex-shrink-0" />
            </button>
          ))}
          {input.trim() && !suggestions.some(s => s.name.toLowerCase() === input.trim().toLowerCase()) && (
            <button
              onClick={() => addCompany(input)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-center gap-2 text-xs text-black/50 border-t border-black/[0.06]"
            >
              <Plus className="w-3 h-3" />
              Add "{input.trim()}" as custom entry
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// PRECISION PANEL — Main Component
// ═══════════════════════════════════════════════════════════

/**
 * PrecisionPanel — Full search interpretation panel for Precision Search
 * 
 * When Precision is ON, this REPLACES the PillBuilder entirely.
 * It interprets the query via AI, shows parsed fields, and lets users refine
 * with constrained, multi-select inputs for every ContactOut parameter.
 *
 * All structured fields use dropdowns/chips with EXACT API values —
 * no free-text for industry, seniority, department, headcount, etc.
 */
function PrecisionPanel({ 
  query, 
  mode = 'people',
  location: locationProp = '',
  onRefinementsChange,
  isSearching = false,
  compact = false,
  initialRefinements = null,
}) {
  // Interpretation state
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [hasInterpreted, setHasInterpreted] = useState(false);
  const [interpretError, setInterpretError] = useState(null);
  const lastQueryRef = useRef('');
  
  // ── Core Fields ──
  const [jobTitles, setJobTitles] = useState([]);   // multi-title (array)
  const [titleInput, setTitleInput] = useState('');  // text input for adding titles
  const [locations, setLocations] = useState([]);    // multi-location
  const [locationInput, setLocationInput] = useState('');

  // ── Constrained Selectors ──
  const [industries, setIndustries] = useState([]);
  const [suggestedIndustries, setSuggestedIndustries] = useState([]);
  const [seniority, setSeniority] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [headcount, setHeadcount] = useState([]);
  const [yearsExperience, setYearsExperience] = useState([]);
  const [countries, setCountries] = useState([]);

  // ── Free-text Multi-value ──
  const [skills, setSkills] = useState([]);
  const [companies, setCompanies] = useState([]);

  // ── Exclusions ──
  const [excludeCompanies, setExcludeCompanies] = useState([]);
  const [excludeTitles, setExcludeTitles] = useState([]);

  // ── Extended ──
  const [keyword, setKeyword] = useState('');
  const [showExtended, setShowExtended] = useState(false);
  
  const interpretTimerRef = useRef(null);
  const initializedFromRefinements = useRef(false);

  // ── Initialize from existing refinements (when used in Refine panel) ──
  useEffect(() => {
    if (!initialRefinements || initializedFromRefinements.current) return;
    initializedFromRefinements.current = true;
    
    if (initialRefinements.jobTitles?.length) {
      setJobTitles(initialRefinements.jobTitles);
    } else if (initialRefinements.jobTitle) {
      setJobTitles(Array.isArray(initialRefinements.jobTitle) ? initialRefinements.jobTitle : [initialRefinements.jobTitle]);
    }
    if (initialRefinements.locations?.length) setLocations(initialRefinements.locations);
    if (initialRefinements.industries?.length) setIndustries(initialRefinements.industries);
    if (initialRefinements.skills?.length) setSkills(initialRefinements.skills);
    if (initialRefinements.companies?.length) setCompanies(initialRefinements.companies);
    if (initialRefinements.seniority?.length) setSeniority(initialRefinements.seniority);
    if (initialRefinements.departments?.length) setDepartments(initialRefinements.departments);
    if (initialRefinements.headcount?.length) setHeadcount(initialRefinements.headcount);
    if (initialRefinements.yearsExperience?.length) setYearsExperience(initialRefinements.yearsExperience);
    if (initialRefinements.countries?.length) setCountries(initialRefinements.countries);
    if (initialRefinements.keyword) setKeyword(initialRefinements.keyword);
    if (initialRefinements.excludeCompanies?.length) setExcludeCompanies(initialRefinements.excludeCompanies);
    if (initialRefinements.excludeTitles?.length) setExcludeTitles(initialRefinements.excludeTitles);
    // If we have extended fields, auto-expand the section
    if (initialRefinements.seniority?.length || initialRefinements.departments?.length ||
        initialRefinements.headcount?.length || initialRefinements.yearsExperience?.length || 
        initialRefinements.companies?.length || initialRefinements.excludeCompanies?.length ||
        initialRefinements.excludeTitles?.length) {
      setShowExtended(true);
    }
    // Mark as already interpreted so we don't overwrite with AI interpretation
    setHasInterpreted(true);
    lastQueryRef.current = (query || '').trim();
  }, [initialRefinements, query]);

  // ── Related suggestions from the People Search interpreter ──
  const [relatedSuggestions, setRelatedSuggestions] = useState({ relatedTitles: [], relatedIndustries: [], relatedSkills: [], relatedSeniority: [], suggestedSeniority: [], suggestedDepartments: [] });
  
  // ── AI interpretation summary (human-readable note) ──
  const [interpretationSummary, setInterpretationSummary] = useState('');

  // ── Estimated result count (live preview) ──
  const [estimatedCount, setEstimatedCount] = useState(null);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const countTimerRef = useRef(null);

  // ── Auto-interpret query using the new People Search interpreter ──
  useEffect(() => {
    const trimmed = (query || '').trim();
    if (trimmed.length < 4 || trimmed === lastQueryRef.current) return;
    
    if (interpretTimerRef.current) clearTimeout(interpretTimerRef.current);
    interpretTimerRef.current = setTimeout(async () => {
      lastQueryRef.current = trimmed;
      setIsInterpreting(true);
      setInterpretError(null);
      
      try {
        const { data } = await api.post('/api/ai/interpret-people-search', {
          query: trimmed,
          location: locationProp || undefined,
          mode: mode || 'people',
        }, { timeout: 15000 });
        
        if (data.success && data.interpretation) {
          const interp = data.interpretation;
          
          // Set core fields — title goes into the multi-title array
          if (interp.jobTitle) setJobTitles([interp.jobTitle]);
          
          // Locations — from AI + any user-specified location
          if (interp.locations?.length > 0) {
            setLocations(interp.locations);
          } else if (locationProp) {
            setLocations([locationProp]);
          }
          
          // Countries — now in core section, no need to expand More Options
          if (interp.countries?.length > 0) {
            setCountries(interp.countries);
          }
          
          // Industries — already validated against taxonomy on the backend
          if (interp.industries?.length > 0) {
            setIndustries(interp.industries);
          }
          
          // Skills — extracted from query
          if (interp.skills?.length > 0) {
            setSkills(interp.skills);
          }
          
          // SENIORITY — auto-apply ONLY when the user explicitly asked for it.
          // "VPs of medical device companies" → explicit → auto-apply as filter
          // "Clinical Operations Manager" → inferred → show as suggestion only
          if (interp.seniorityIsExplicit && interp.seniority?.length > 0) {
            setSeniority(interp.seniority);
            // Auto-expand More Options so user can see seniority is active
            setShowExtended(true);
          }
          // Departments — always suggestions only (inferred from context)
          
          // Companies — from AI (e.g., FAANG already expanded on backend)
          if (interp.companies?.length > 0) {
            setCompanies(interp.companies);
          }
          
          // Company headcount — from descriptor (startup, enterprise, etc.)
          if (interp.companyHeadcount?.length > 0) {
            setHeadcount(interp.companyHeadcount);
            setShowExtended(true); // Show headcount section
          }
          
          // Keywords — now in core section
          if (interp.keywords?.length > 0) {
            setKeyword(interp.keywords.join(', '));
          }
          
          // AI interpretation summary — show what the AI understood
          if (interp.summary) {
            setInterpretationSummary(interp.summary);
          } else {
            setInterpretationSummary('');
          }
          
          // Related suggestions — display to the user, grouped by category
          if (interp.suggestions) {
            setRelatedSuggestions({
              ...interp.suggestions,
              relatedSeniority: interp.suggestions.relatedSeniority || [],
              // Only show seniority/department as suggestions if NOT already auto-applied
              suggestedSeniority: interp.seniorityIsExplicit ? [] : (interp.seniority || []),
              suggestedDepartments: interp.departments || [],
            });
            
            // Also populate suggested industries from the interpreter
            const suggested = [...(interp.suggestions.relatedIndustries || [])];
            // Augment with local groups
            (interp.industries || []).forEach(ind => {
              const related = INDUSTRY_GROUPS[ind] || [];
              related.forEach(r => {
                if (!(interp.industries || []).includes(r) && !suggested.includes(r)) {
                  suggested.push(r);
                }
              });
            });
            setSuggestedIndustries(suggested.slice(0, 6));
          }
          
          // Companies — extracted from query (e.g., "at Google", "at FAANG companies")
          if (interp.companies?.length > 0) {
            setCompanies(interp.companies);
          }
          
          // Company headcount — derived from descriptors like "startup", "enterprise"
          if (interp.companyHeadcount?.length > 0) {
            setHeadcount(interp.companyHeadcount);
            setShowExtended(true); // Show extended options so user sees headcount is active
          }
          
          setHasInterpreted(true);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
          console.error('People search interpretation error:', err);
          if (mode === 'company') {
            console.log('Company mode: interpretation failed, filters available for manual input');
          } else {
            setInterpretError('Could not interpret query');
          }
        }
      } finally {
        setIsInterpreting(false);
      }
    }, 600);
    
    return () => {
      if (interpretTimerRef.current) clearTimeout(interpretTimerRef.current);
    };
  }, [query, mode, locationProp]);

  // ── Notify parent of refinement changes ──
  useEffect(() => {
    if (onRefinementsChange) {
      onRefinementsChange({
        jobTitle: jobTitles.length > 0 ? jobTitles[0] : undefined, // backward compat: first title as string
        jobTitles: jobTitles.length > 0 ? jobTitles : undefined,   // full array for multi-title search
        locations: locations.length > 0 ? locations : undefined,
        location: locations[0] || undefined,
        skills: skills.length > 0 ? skills : undefined,
        industries: industries.length > 0 ? industries : undefined,
        companies: companies.length > 0 ? companies : undefined,
        keyword: keyword.trim() || undefined,
        seniority: seniority.length > 0 ? seniority : undefined,
        departments: departments.length > 0 ? departments : undefined,
        headcount: headcount.length > 0 ? headcount : undefined,
        yearsExperience: yearsExperience.length > 0 ? yearsExperience : undefined,
        countries: countries.length > 0 ? countries : undefined,
        excludeCompanies: excludeCompanies.length > 0 ? excludeCompanies : undefined,
        excludeTitles: excludeTitles.length > 0 ? excludeTitles : undefined,
      });
    }
  }, [skills, industries, companies, jobTitles, locations, keyword, seniority, departments, headcount, yearsExperience, countries, excludeCompanies, excludeTitles, onRefinementsChange]);

  // ── Live estimated count (debounced) ──
  // Field names MUST match what buildContactOutSearchPayload expects (camelCase internal names)
  useEffect(() => {
    const criteria = {};
    if (jobTitles.length > 0) criteria.jobTitles = jobTitles;
    if (industries.length > 0) criteria.industry = industries;
    if (seniority.length > 0) criteria.seniority = seniority;
    if (departments.length > 0) criteria.departments = departments;
    if (skills.length > 0) criteria.skills = skills;
    if (keyword.trim()) criteria.keyword = keyword.trim();
    if (companies.length > 0) criteria.companyName = companies;
    if (headcount.length > 0) criteria.headcount = headcount;
    if (yearsExperience.length > 0) criteria.yearsExperience = yearsExperience;
    if (countries.length > 0) criteria.countries = countries;
    if (locations.length > 0) criteria.location = locations;

    // Only fire if we have at least one meaningful filter
    const hasAnyCriteria = Object.keys(criteria).length > 0;
    if (!hasAnyCriteria) {
      setEstimatedCount(null);
      return;
    }

    if (countTimerRef.current) clearTimeout(countTimerRef.current);
    countTimerRef.current = setTimeout(async () => {
      setIsCountLoading(true);
      try {
        const { data } = await api.post('/api/ai/contactout-count', { criteria }, { timeout: 12000 });
        if (data.success && data.totalResults != null) {
          setEstimatedCount(data.totalResults);
        }
      } catch (e) {
        console.warn('Count estimate failed:', e.message);
      } finally {
        setIsCountLoading(false);
      }
    }, 800);

    return () => { if (countTimerRef.current) clearTimeout(countTimerRef.current); };
  }, [jobTitles, industries, seniority, departments, skills, keyword, companies, headcount, yearsExperience, countries, locations]);

  // ── Title tag handlers (supports "-term" prefix for exclusions) ──
  const addTitle = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Detect exclusion prefix: "-Manager" or "- Manager"
    if (trimmed.startsWith('-')) {
      const excluded = trimmed.replace(/^-\s*/, '').trim();
      if (excluded && !excludeTitles.includes(excluded)) {
        setExcludeTitles(prev => [...prev, excluded]);
      }
    } else if (!jobTitles.includes(trimmed)) {
      setJobTitles(prev => [...prev, trimmed]);
    }
    setTitleInput('');
  }, [jobTitles, excludeTitles]);

  const removeTitle = useCallback((title) => {
    setJobTitles(prev => prev.filter(t => t !== title));
  }, []);

  const removeExcludedTitle = useCallback((title) => {
    setExcludeTitles(prev => prev.filter(t => t !== title));
  }, []);

  // Toggle title between include ↔ exclude (click chip to toggle)
  const toggleTitleExclude = useCallback((title) => {
    if (jobTitles.includes(title)) {
      setJobTitles(prev => prev.filter(t => t !== title));
      setExcludeTitles(prev => [...prev, title]);
    } else if (excludeTitles.includes(title)) {
      setExcludeTitles(prev => prev.filter(t => t !== title));
      setJobTitles(prev => [...prev, title]);
    }
  }, [jobTitles, excludeTitles]);

  // Toggle company between include ↔ exclude
  const toggleCompanyExclude = useCallback((name) => {
    if (companies.includes(name)) {
      setCompanies(prev => prev.filter(c => c !== name));
      setExcludeCompanies(prev => [...prev, name]);
    } else if (excludeCompanies.includes(name)) {
      setExcludeCompanies(prev => prev.filter(c => c !== name));
      setCompanies(prev => [...prev, name]);
    }
  }, [companies, excludeCompanies]);

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (titleInput.trim()) addTitle(titleInput);
    }
    if (e.key === 'Backspace' && !titleInput) {
      if (excludeTitles.length > 0) {
        setExcludeTitles(prev => prev.slice(0, -1));
      } else if (jobTitles.length > 0) {
        setJobTitles(prev => prev.slice(0, -1));
      }
    }
  };

  // ── Location tag handlers ──
  const addLocation = useCallback((text) => {
    const items = text.split(',').map(s => s.trim()).filter(s => s && !locations.includes(s));
    if (items.length > 0) setLocations(prev => [...prev, ...items]);
    setLocationInput('');
  }, [locations]);

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (locationInput.trim()) addLocation(locationInput);
    }
    if (e.key === 'Backspace' && !locationInput && locations.length > 0) {
      setLocations(prev => prev.slice(0, -1));
    }
  };

  const removeLocation = useCallback((loc) => {
    setLocations(prev => prev.filter(l => l !== loc));
  }, []);

  // Manage industry suggestions when industries change
  const handleIndustrySuggestionDismiss = useCallback((item) => {
    setSuggestedIndustries(prev => prev.filter(s => s !== item));
  }, []);

  useEffect(() => {
    const suggestions = new Set();
    industries.forEach(ind => {
      const related = INDUSTRY_GROUPS[ind] || [];
      related.forEach(r => {
        if (!industries.includes(r)) suggestions.add(r);
      });
    });
    setSuggestedIndustries([...suggestions].slice(0, 6));
  }, [industries]);

  if (!query?.trim() && mode !== 'company') return null;

  // Count how many "more options" fields are active
  const moreOptionsCount = [seniority, departments, companies, headcount, yearsExperience]
    .filter(arr => arr.length > 0).length;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className={`flex items-center justify-center w-7 h-7 rounded-xl shadow-sm ${
            mode === 'company' 
              ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-indigo-500/20' 
              : 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/20'
          }`}>
            {mode === 'company' ? <Building2 className="w-4 h-4 text-white" /> : <Target className="w-4 h-4 text-white" />}
          </div>
          <div>
            <span 
              className="text-sm font-semibold text-black/80"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {mode === 'company' ? 'Company Search' : 'People Search'}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-2 ${
              mode === 'company' 
                ? 'text-indigo-600 bg-indigo-50' 
                : 'text-emerald-600 bg-emerald-50'
            }`}>
              {mode === 'company' ? 'Find contacts at target companies' : 'Extended'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Estimated result count */}
          {estimatedCount != null && !isInterpreting && (
            <div className="flex items-center gap-1 text-[11px] font-medium text-black/50 bg-black/[0.03] rounded-full px-2 py-0.5">
              <BarChart3 className="w-3 h-3" />
              <span>~{estimatedCount.toLocaleString()} results</span>
            </div>
          )}
          {isCountLoading && !isInterpreting && (
            <div className="flex items-center gap-1 text-[11px] text-black/30">
              <Loader2 className="w-3 h-3 animate-spin" />
            </div>
          )}
          {isInterpreting && (
            <div className="flex items-center gap-1.5 text-emerald-600">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span className="text-[11px] font-medium">Interpreting...</span>
            </div>
          )}
        </div>
      </div>

      {/* Interpretation error — hidden in company mode (filters are the primary input) */}
      {interpretError && mode !== 'company' && (
        <div className="text-xs text-red-500 mb-2">{interpretError}</div>
      )}

      {/* ═══════ AI INTERPRETATION SUMMARY ═══════ */}
      {hasInterpreted && interpretationSummary && !isInterpreting && (
        <div className="flex items-start gap-2 bg-emerald-50/50 rounded-lg px-3 py-2 border border-emerald-100/60 mb-2.5 animate-in fade-in duration-200">
          <MessageSquare className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-black/60 leading-relaxed"
             dangerouslySetInnerHTML={{ __html: interpretationSummary
               .replace(/\*\*(.+?)\*\*/g, '<strong class="text-black/80">$1</strong>')
               .replace(/_(.+?)_/g, '<em class="text-amber-600">$1</em>')
             }}
          />
        </div>
      )}

      {/* ═══════ SUGGESTIONS (grouped by category) ═══════ */}
      {hasInterpreted && (() => {
        const titleSugs = relatedSuggestions.relatedTitles?.filter(t => !jobTitles.includes(t)) || [];
        const senSugs = relatedSuggestions.suggestedSeniority?.filter(s => !seniority.includes(s)) || [];
        const addSenSugs = relatedSuggestions.relatedSeniority?.filter(s => !seniority.includes(s) && !senSugs.includes(s)) || [];
        const deptSugs = relatedSuggestions.suggestedDepartments?.filter(d => !departments.includes(d)) || [];
        const skillSugs = relatedSuggestions.relatedSkills?.filter(s => !skills.includes(s)) || [];
        const hasSuggestions = titleSugs.length > 0 || senSugs.length > 0 || addSenSugs.length > 0 || deptSugs.length > 0 || skillSugs.length > 0;
        
        if (!hasSuggestions) return null;
        
        return (
          <div className="bg-gradient-to-r from-amber-50/60 to-orange-50/30 rounded-xl p-2.5 border border-amber-100/50 mb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-semibold text-amber-700/70 uppercase tracking-wider">You might also want</span>
            </div>
            <div className="space-y-1.5">
              {/* Titles group */}
              {titleSugs.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-[9px] text-black/30 uppercase tracking-wider font-medium mt-1 w-12 flex-shrink-0">Titles</span>
                  <div className="flex flex-wrap gap-1">
                    {titleSugs.map((title) => (
                      <button
                        key={`t-${title}`}
                        onClick={() => setJobTitles(prev => [...prev, title])}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100/60 text-amber-700 hover:bg-amber-200/60 transition-colors border border-amber-200/40"
                      >
                        <Plus className="w-2.5 h-2.5 inline mr-0.5" />{title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Seniority group (inferred — user opts in) */}
              {(senSugs.length > 0 || addSenSugs.length > 0) && (
                <div className="flex items-start gap-1.5">
                  <span className="text-[9px] text-black/30 uppercase tracking-wider font-medium mt-1 w-12 flex-shrink-0">Level</span>
                  <div className="flex flex-wrap gap-1">
                    {senSugs.map((level) => {
                      const opt = SENIORITY_OPTIONS.find(o => o.value === level);
                      const label = opt ? opt.label : level;
                      return (
                        <button
                          key={`sen-${level}`}
                          onClick={() => { setSeniority(prev => [...prev, level]); setShowExtended(true); }}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100/80 text-slate-600 hover:bg-amber-100/60 hover:text-amber-700 transition-colors border border-slate-200/60"
                        >
                          <Layers className="w-2.5 h-2.5 inline mr-0.5 opacity-50" />{label}
                        </button>
                      );
                    })}
                    {addSenSugs.map((level) => {
                      const opt = SENIORITY_OPTIONS.find(o => o.value === level);
                      const label = opt ? opt.label : level;
                      return (
                        <button
                          key={`addsen-${level}`}
                          onClick={() => { setSeniority(prev => [...prev, level]); setShowExtended(true); }}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50/80 text-slate-500 hover:bg-amber-100/60 hover:text-amber-700 transition-colors border border-dashed border-slate-200/60"
                        >
                          <Plus className="w-2.5 h-2.5 inline mr-0.5 opacity-40" />{label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Department group */}
              {deptSugs.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-[9px] text-black/30 uppercase tracking-wider font-medium mt-1 w-12 flex-shrink-0">Dept</span>
                  <div className="flex flex-wrap gap-1">
                    {deptSugs.map((dept) => {
                      const opt = DEPARTMENT_OPTIONS.find(o => (typeof o === 'string' ? o : o.value) === dept);
                      const label = opt ? (typeof opt === 'string' ? opt : opt.label) : dept;
                      return (
                        <button
                          key={`dept-${dept}`}
                          onClick={() => { setDepartments(prev => [...prev, dept]); setShowExtended(true); }}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50/80 text-blue-600 hover:bg-blue-100/60 hover:text-blue-700 transition-colors border border-blue-200/40"
                        >
                          <Users className="w-2.5 h-2.5 inline mr-0.5 opacity-50" />{label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Skills group */}
              {skillSugs.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <span className="text-[9px] text-black/30 uppercase tracking-wider font-medium mt-1 w-12 flex-shrink-0">Skills</span>
                  <div className="flex flex-wrap gap-1">
                    {skillSugs.map((skill) => (
                      <button
                        key={`s-${skill}`}
                        onClick={() => setSkills(prev => [...prev, skill])}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100/60 text-violet-700 hover:bg-violet-200/60 transition-colors border border-violet-200/40"
                      >
                        <Plus className="w-2.5 h-2.5 inline mr-0.5" />{skill}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══════ CORE FIELDS ═══════ */}
      {mode === 'company' ? (
        <>
          {/* ── COMPANY MODE: Company-first layout ── */}
          <div className="bg-gradient-to-r from-slate-50/80 to-indigo-50/30 rounded-xl p-3 border border-black/[0.04] mb-2.5">
            <div className="flex items-center gap-1.5 mb-2.5">
              <Building2 className="w-3 h-3 text-indigo-500" />
              <span className="text-[10px] font-semibold text-indigo-600/70 uppercase tracking-wider">Target Companies</span>
            </div>

            {/* Industry (PRIMARY — most prominent) */}
            <div className="mb-2.5">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Factory className="w-2.5 h-2.5" /> Industry
              </label>
              <SearchableMultiSelect
                options={INDUSTRY_OPTIONS}
                selected={industries}
                onChange={setIndustries}
                placeholder="Search industries... e.g., Biotechnology"
                color="emerald"
                icon={Factory}
                suggestions={suggestedIndustries}
                onSuggestionsDismiss={handleIndustrySuggestionDismiss}
              />
            </div>

            {/* Company Size / Headcount (promoted to core) */}
            <div className="mb-2.5">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <BarChart3 className="w-2.5 h-2.5" /> Company Size
              </label>
              <ChipSelector
                options={HEADCOUNT_OPTIONS}
                selected={headcount}
                onChange={setHeadcount}
                color="slate"
                size="xs"
              />
            </div>

            {/* Country (promoted to core for company mode) */}
            <div className="mb-2.5">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Globe className="w-2.5 h-2.5" /> Country
              </label>
              <SearchableMultiSelect
                options={ALL_COUNTRIES}
                selected={countries}
                onChange={setCountries}
                placeholder="Search countries..."
                color="blue"
                icon={Globe}
                pinnedOptions={TOP_COUNTRIES}
              />
            </div>

            {/* Location (city/region) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <MapPin className="w-2.5 h-2.5" /> Location
                <span className="text-[9px] text-black/25 font-normal ml-1">city or region</span>
              </label>
              {locations.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {locations.map((loc) => (
                    <span key={loc} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-100 text-teal-700 border border-teal-200">
                      <MapPin className="w-2.5 h-2.5" />
                      {loc}
                      <button onClick={() => removeLocation(loc)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={handleLocationKeyDown}
                  className="w-full text-sm bg-white border border-black/[0.06] rounded-lg pl-7 pr-3 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 transition-all"
                  placeholder={locations.length > 0 ? 'Add another location...' : 'e.g., Boston, San Francisco, EMEA'}
                />
                <MapPin className="w-3 h-3 text-black/20 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Specific Companies (optional refinement) */}
            <div>
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Building2 className="w-2.5 h-2.5" /> Specific Companies
                <span className="text-[9px] text-blue-500 bg-blue-50 px-1 py-0.5 rounded font-medium ml-1">optional</span>
              </label>
              <CompanyAutocomplete
                values={companies}
                onChange={setCompanies}
                excludedValues={excludeCompanies}
                onExcludeChange={setExcludeCompanies}
                onToggleExclude={toggleCompanyExclude}
                placeholder="Target specific companies... e.g., Moderna"
                color="blue"
              />
            </div>
          </div>

          {/* ── WHO TO TARGET section ── */}
          <div className="bg-gradient-to-r from-slate-50/80 to-amber-50/20 rounded-xl p-3 border border-black/[0.04] mb-2.5">
            <div className="flex items-center gap-1.5 mb-2.5">
              <Target className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-semibold text-amber-600/70 uppercase tracking-wider">Who to Target</span>
            </div>

            {/* Seniority (promoted — primary filter for "who") */}
            <div className="mb-2.5">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Layers className="w-2.5 h-2.5" /> Seniority Level
              </label>
              <ChipSelector
                options={SENIORITY_OPTIONS.filter(o => ['Owner / Founder', 'CXO', 'Partner', 'VP', 'Head', 'Director', 'Manager'].includes(o.value))}
                selected={seniority}
                onChange={setSeniority}
                color="amber"
                size="xs"
              />
            </div>

            {/* Title (secondary in company mode) */}
            <div className="mb-2.5">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Briefcase className="w-2.5 h-2.5" /> Job Titles
                <span className="text-[9px] text-black/25 font-normal ml-1">optional — narrow by role</span>
              </label>
              {(jobTitles.length > 0 || excludeTitles.length > 0) && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {jobTitles.map((title) => (
                    <span key={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-200/70 transition-colors group" title="Click to exclude">
                      <Briefcase className="w-2.5 h-2.5" />
                      <span onClick={() => toggleTitleExclude(title)}>{title}</span>
                      <button onClick={() => removeTitle(title)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {excludeTitles.map((title) => (
                    <span key={`excl-${title}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700 border border-red-200 line-through decoration-red-400/50 cursor-pointer hover:bg-red-200/70 transition-colors" title="Click to include">
                      <MinusCircle className="w-2.5 h-2.5" />
                      <span onClick={() => toggleTitleExclude(title)}>{title}</span>
                      <button onClick={() => removeExcludedTitle(title)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                className="w-full text-sm bg-white border border-black/[0.06] rounded-lg px-2.5 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200 transition-all"
                placeholder={jobTitles.length > 0 ? 'Add another title...' : 'e.g., VP of R&D, Chief Scientific Officer'}
              />
            </div>

            {/* Department / Function */}
            <div>
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Users className="w-2.5 h-2.5" /> Department
              </label>
              <SearchableMultiSelect
                options={DEPARTMENT_OPTIONS.map(o => typeof o === 'string' ? o : o.label)}
                selected={departments.map(val => {
                  const opt = DEPARTMENT_OPTIONS.find(o => (typeof o === 'string' ? o : o.value) === val);
                  return opt ? (typeof opt === 'string' ? opt : opt.label) : val;
                })}
                onChange={(labels) => {
                  const values = labels.map(label => {
                    const opt = DEPARTMENT_OPTIONS.find(o => (typeof o === 'string' ? o : o.label) === label);
                    return opt ? (typeof opt === 'string' ? opt : opt.value) : label;
                  });
                  setDepartments(values);
                }}
                placeholder="Select departments..."
                color="blue"
                icon={Users}
              />
            </div>
          </div>

          {/* ── MORE OPTIONS (company mode) ── */}
          <button 
            onClick={() => setShowExtended(!showExtended)}
            className="flex items-center gap-1.5 w-full text-[11px] text-black/40 hover:text-black/60 transition-colors mb-2 py-1"
          >
            <SlidersHorizontal className="w-3 h-3" />
            <span>{showExtended ? 'Hide' : 'More'} options</span>
            {showExtended ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
          </button>

          {showExtended && (
            <div className="bg-slate-50/50 rounded-xl p-3 border border-black/[0.03] mb-2.5 animate-in fade-in duration-200 space-y-2.5">
              {/* Skills */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Zap className="w-2.5 h-2.5" /> Skills
                </label>
                <TagInput
                  values={skills}
                  onChange={setSkills}
                  placeholder="e.g., GCP, Clinical Trials, FDA (comma to add)"
                  color="violet"
                  icon={Zap}
                />
              </div>

              {/* Profile Keyword */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Search className="w-2.5 h-2.5" /> Profile Keyword
                  <span className="text-[9px] text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded font-medium ml-1">deep search</span>
                </label>
                <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
                  className="w-full text-xs bg-white border border-black/[0.06] rounded-lg px-2.5 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-indigo-300 focus:ring-1 focus:ring-indigo-200 transition-all"
                  placeholder="e.g., gene therapy, cell therapy, mRNA" />
              </div>

              {/* Years of Experience */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Clock className="w-2.5 h-2.5" /> Years of Experience
                </label>
                <ChipSelector
                  options={EXPERIENCE_OPTIONS}
                  selected={yearsExperience}
                  onChange={setYearsExperience}
                  color="emerald"
                  size="xs"
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── PEOPLE MODE: Original layout ── */}
          <div className="bg-gradient-to-r from-slate-50/80 to-emerald-50/30 rounded-xl p-3 border border-black/[0.04] mb-2.5">
            {/* Title (multi — tag input style) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Briefcase className="w-2.5 h-2.5" /> Titles
                {jobTitles.length > 1 && (
                  <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded font-medium ml-1">
                    {jobTitles.length} titles — matches any
                  </span>
                )}
              </label>
              {(jobTitles.length > 0 || excludeTitles.length > 0) && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {jobTitles.map((title) => (
                    <span key={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-200/70 transition-colors group" title="Click to exclude">
                      <Briefcase className="w-2.5 h-2.5" />
                      <span onClick={() => toggleTitleExclude(title)}>{title}</span>
                      <button onClick={() => removeTitle(title)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {excludeTitles.map((title) => (
                    <span key={`excl-${title}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700 border border-red-200 line-through decoration-red-400/50 cursor-pointer hover:bg-red-200/70 transition-colors" title="Click to include">
                      <MinusCircle className="w-2.5 h-2.5" />
                      <span onClick={() => toggleTitleExclude(title)}>{title}</span>
                      <button onClick={() => removeExcludedTitle(title)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                className="w-full text-sm bg-white border border-black/[0.06] rounded-lg px-2.5 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200 transition-all"
                placeholder={jobTitles.length > 0 ? 'Add another title...' : 'e.g., Clinical Operations Manager'}
              />
            </div>

            {/* Location (multi) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <MapPin className="w-2.5 h-2.5" /> Locations
                <span className="text-[9px] text-black/25 font-normal ml-1">comma or enter to add</span>
              </label>
              {locations.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {locations.map((loc) => (
                    <span key={loc} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-100 text-teal-700 border border-teal-200">
                      <MapPin className="w-2.5 h-2.5" />
                      {loc}
                      <button onClick={() => removeLocation(loc)} className="opacity-60 hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={handleLocationKeyDown}
                  className="w-full text-sm bg-white border border-black/[0.06] rounded-lg pl-7 pr-3 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200 transition-all"
                  placeholder={locations.length > 0 ? 'Add another location...' : 'e.g., Boston, London, EMEA'}
                />
                <MapPin className="w-3 h-3 text-black/20 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Industry (Searchable Dropdown) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Factory className="w-2.5 h-2.5" /> Industries
              </label>
              <SearchableMultiSelect
                options={INDUSTRY_OPTIONS}
                selected={industries}
                onChange={setIndustries}
                placeholder="Search industries..."
                color="emerald"
                icon={Factory}
                suggestions={suggestedIndustries}
                onSuggestionsDismiss={handleIndustrySuggestionDismiss}
              />
            </div>

            {/* Skills (Free-text Tags) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Zap className="w-2.5 h-2.5" /> Skills
              </label>
              <TagInput
                values={skills}
                onChange={setSkills}
                placeholder="e.g., GCP, Clinical Trials, FDA (comma to add)"
                color="violet"
                icon={Zap}
              />
            </div>

            {/* Profile Keyword (full-profile deep search) */}
            <div className="mb-2">
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Search className="w-2.5 h-2.5" /> Profile Keyword
                <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded font-medium ml-1">deep search</span>
              </label>
              <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
                className="w-full text-xs bg-white border border-black/[0.06] rounded-lg px-2.5 py-1.5 text-black/80 placeholder-black/25 focus:outline-none focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200 transition-all"
                placeholder="Search across full profiles: bio, experience, specializations" />
            </div>

            {/* Country (Searchable Dropdown) */}
            <div>
              <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                <Globe className="w-2.5 h-2.5" /> Country
              </label>
              <SearchableMultiSelect
                options={ALL_COUNTRIES}
                selected={countries}
                onChange={setCountries}
                placeholder="Search countries..."
                color="blue"
                icon={Globe}
                pinnedOptions={TOP_COUNTRIES}
              />
            </div>
          </div>

          {/* ═══════ MORE OPTIONS TOGGLE ═══════ */}
          <button 
            onClick={() => setShowExtended(!showExtended)}
            className="flex items-center gap-1.5 w-full text-[11px] text-black/40 hover:text-black/60 transition-colors mb-2 py-1"
          >
            <SlidersHorizontal className="w-3 h-3" />
            <span>{showExtended ? 'Hide' : 'More'} options</span>
            {moreOptionsCount > 0 && !showExtended && (
              <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full font-medium">
                {moreOptionsCount} active
              </span>
            )}
            {showExtended ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
          </button>

          {showExtended && (
            <div className="bg-slate-50/50 rounded-xl p-3 border border-black/[0.03] mb-2.5 animate-in fade-in duration-200 space-y-2.5">
              {/* Seniority (Searchable Dropdown) */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Layers className="w-2.5 h-2.5" /> Seniority Level
                </label>
                <SearchableMultiSelect
                  options={SENIORITY_OPTIONS.map(o => o.label)}
                  selected={seniority.map(val => {
                    const opt = SENIORITY_OPTIONS.find(o => o.value === val);
                    return opt ? opt.label : val;
                  })}
                  onChange={(labels) => {
                    const values = labels.map(label => {
                      const opt = SENIORITY_OPTIONS.find(o => o.label === label);
                      return opt ? opt.value : label;
                    });
                    setSeniority(values);
                  }}
                  placeholder="Select seniority levels..."
                  color="amber"
                  icon={Layers}
                />
              </div>

              {/* Department / Function (Searchable Dropdown) */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Users className="w-2.5 h-2.5" /> Department / Function
                </label>
                <SearchableMultiSelect
                  options={DEPARTMENT_OPTIONS.map(o => typeof o === 'string' ? o : o.label)}
                  selected={departments.map(val => {
                    const opt = DEPARTMENT_OPTIONS.find(o => (typeof o === 'string' ? o : o.value) === val);
                    return opt ? (typeof opt === 'string' ? opt : opt.label) : val;
                  })}
                  onChange={(labels) => {
                    const values = labels.map(label => {
                      const opt = DEPARTMENT_OPTIONS.find(o => (typeof o === 'string' ? o : o.label) === label);
                      return opt ? (typeof opt === 'string' ? opt : opt.value) : label;
                    });
                    setDepartments(values);
                  }}
                  placeholder="Select departments..."
                  color="blue"
                  icon={Users}
                />
              </div>

              {/* Target Companies (ZoomInfo-powered autocomplete) */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Building2 className="w-2.5 h-2.5" /> Target Companies
                  <span className="text-[9px] text-blue-500 bg-blue-50 px-1 py-0.5 rounded font-medium ml-1">autocomplete</span>
                </label>
                <CompanyAutocomplete
                  values={companies}
                  onChange={setCompanies}
                  excludedValues={excludeCompanies}
                  onExcludeChange={setExcludeCompanies}
                  onToggleExclude={toggleCompanyExclude}
                  placeholder="Search companies e.g., Medtronic..."
                  color="blue"
                />
              </div>

              {/* Company Headcount */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <BarChart3 className="w-2.5 h-2.5" /> Company Headcount
                </label>
                <ChipSelector
                  options={HEADCOUNT_OPTIONS}
                  selected={headcount}
                  onChange={setHeadcount}
                  color="slate"
                  size="xs"
                />
              </div>

              {/* Years of Experience */}
              <div>
                <label className="text-[10px] text-black/40 uppercase tracking-wider font-medium flex items-center gap-1 mb-1">
                  <Clock className="w-2.5 h-2.5" /> Years of Experience
                </label>
                <ChipSelector
                  options={EXPERIENCE_OPTIONS}
                  selected={yearsExperience}
                  onChange={setYearsExperience}
                  color="emerald"
                  size="xs"
                />
              </div>

              {/* Exclusions integrated into Titles and Companies fields above (use - prefix) */}

            </div>
          )}
        </>
      )}

      {/* Capabilities footer */}
      <div className="flex items-center gap-3 pt-1.5 border-t border-black/5">
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${mode === 'company' ? 'bg-indigo-400' : 'bg-emerald-400'}`}></div>
          <span className="text-[10px] text-black/30">{mode === 'company' ? 'Industry filtering' : 'Skills matching'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${mode === 'company' ? 'bg-indigo-400' : 'bg-emerald-400'}`}></div>
          <span className="text-[10px] text-black/30">Full profile data</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${mode === 'company' ? 'bg-indigo-400' : 'bg-emerald-400'}`}></div>
          <span className="text-[10px] text-black/30">{mode === 'company' ? 'Company intelligence' : 'Enriched profiles'}</span>
        </div>
      </div>
    </div>
  );
}

export default PrecisionPanel;
